import AceModeJ1QL from '@jupiterone/jupiter-web-toolkit/src/util/aceModeJ1QL';
import { useEffect, useMemo, useRef, useState } from 'react';
import { j1qlAutocomplete } from '@jupiterone/jupiter-web-toolkit';

export interface HookProps {
  autocomplete: boolean;
  hasFocus: boolean;
  onChange: (value: string) => void;
  onSearch: VoidFunction;
  onBlur?: VoidFunction;
  onFocus?: VoidFunction;
}

const useSearchAceEditorLogic = ({
  autocomplete,
  hasFocus,
  onSearch,
  onChange,
  onBlur,
  onFocus,
}: HookProps) => {
  const MAX_SIZE_INPUT = 74;
  const editorRef = useRef<j1qlAutocomplete.aceCompleter.AceEditor>(null);
  const [ready, setReady] = useState<boolean>(false);
  const collapseText = !hasFocus;
  // Ben's note: Dumb, but you have to rebind the onSearch command every time.
  // I read through the react ace code and it's only bound and updated during componentdidmount for some reason??? WHY!?
  if (editorRef.current) {
    editorRef.current.editor.commands.commandKeyBinding.return.exec = onSearch;
  }

  // set editor styling mode
  useEffect(() => {
    const j1qlMode = new AceModeJ1QL();

    const editor = editorRef.current?.editor;
    if (ready || !editor) return;
    editor.getSession().setMode(j1qlMode);
    editor.commands.removeCommand('find');
    setReady(true);
  }, []);

  const aceCommands = useMemo(
    () => [
      {
        name: 'submitQuery',
        bindKey: { win: 'Return', mac: 'Return' },
        // overwritten by setting ref command key to search handler prop
        exec: () => onSearch(),
      },
      {
        name: 'addNewLine',
        bindKey: { win: 'Shift-Return', mac: 'Shift-Return' },
        exec: (editor: any) => editor.insert('\n'),
      },
    ],
    [onSearch]
  );

  const handleBlur = () => {
    onBlur?.();
  };

  const handleFocus = () => {
    onFocus?.();
  };

  const handleChange = (text: string) => {
    const newText = text.replace(/\r?\n|\r/g, '\n').replace(/\t/g, ' ');
    if (newText.length > MAX_SIZE_INPUT) {
      editorRef.current?.editor.resize();
    }
    onChange(newText);
  };

  return {
    editorRef,
    aceCommands,
    collapseText: collapseText,
    handleChange,
    handleFocus,
    handleBlur,
  };
};

export default useSearchAceEditorLogic;
